import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, isNotEmptyArray, isNotNil, Nillable } from '@web/utils';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import {
  AdminCosmeticAttributeDetails,
  AdminCosmeticCharacteristicDetails,
  AdminCosmeticDataSourceService,
  AdminCosmeticDetails,
} from '@web/shared/module/cosmetic';
import {
  CurrencyComponent,
  DateComponent,
  DetailsViewComponent,
  ExternalLinkComponent,
  NoDataComponent,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { AdminCosmeticStatusComponent } from '../../components/admin-cosmetic-status/admin-cosmetic-status.component';
import {
  AdminMultimediaDataSourceService,
  MultimediaGalleriaThumbnailsComponent,
} from '@web/shared/module/multimedia';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import { SummernoteHtmlPreviewComponent } from '@web/shared/external';

@Component({
  selector: 'app-admin-cosmetic-details',
  imports: [
    CommonModule,
    SpinnerFilledComponent,
    CurrencyComponent,
    DetailsViewComponent,
    NoDataComponent,
    DateComponent,
    AdminCosmeticStatusComponent,
    MultimediaGalleriaThumbnailsComponent,
    ExternalLinkComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    SummernoteHtmlPreviewComponent,
  ],
  templateUrl: './admin-cosmetic-details.component.html',
  styleUrl: './admin-cosmetic-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminCosmeticDetailsComponent {
  private readonly cosmeticDataSource = inject(AdminCosmeticDataSourceService);
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);

  public readonly id = input.required<Guid>();

  protected readonly id$ = toObservable(this.id);
  protected readonly details$ = this.detailsSource();
  protected readonly isNotNil = isNotNil;
  /* prettier-ignore */
  protected readonly fetchMultimediaDetailsFn = (id: Guid) => this.multimediaDataSource.fetchDetailsById(id, true);

  public shouldShowCharacteristicsAccordions(
    characteristics: Nillable<AdminCosmeticCharacteristicDetails[]>,
  ): characteristics is AdminCosmeticCharacteristicDetails[] {
    return isNotNil(characteristics) && isNotEmptyArray(characteristics);
  }

  public shouldShowAttributesAccordions(
    attributes: Nillable<AdminCosmeticAttributeDetails[]>,
  ): attributes is AdminCosmeticAttributeDetails[] {
    return isNotNil(attributes) && isNotEmptyArray(attributes);
  }

  private detailsSource(): Observable<AdminCosmeticDetails> {
    return this.id$.pipe(
      switchMap((id) => this.cosmeticDataSource.fetchDetailsById(id)),
    );
  }
}
